import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { registerInput } from '@brechoflow/contracts';
import type { z } from 'zod';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { TermsCheckbox } from '@/features/legal/terms-checkbox';
import { applyServerErrors, contractResolver } from '@/lib/forms';
import { authApi } from '../auth-api';
import { useSession } from '../session';

type Values = z.input<typeof registerInput>;

function browserTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz.startsWith('America/') ? tz : 'America/Sao_Paulo';
  } catch {
    return 'America/Sao_Paulo';
  }
}

export function RegisterPage() {
  const { setMe } = useSession();
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<Values, unknown, z.output<typeof registerInput>>({
    resolver: contractResolver(registerInput),
    defaultValues: { name: '', email: '', password: '', organizationName: '', timezone: browserTimezone() },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await authApi.register(values);
      setMe(await authApi.me());
      navigate('/', { replace: true });
    } catch (e) {
      setFormError(applyServerErrors(e, setError));
    }
  });

  return (
    <>
      <h1 className="font-display text-3xl font-semibold">Cadastrar minha loja</h1>
      <p className="mt-1.5 text-sm text-muted-foreground">Você será o proprietário e poderá convidar a equipe depois.</p>
      <form onSubmit={onSubmit} noValidate className="mt-7 flex flex-col gap-4">
        {formError && (
          <p role="alert" className="rounded-control bg-destructive-soft px-3 py-2.5 text-sm text-destructive">
            {formError}
          </p>
        )}
        <FormField label="Nome da loja" error={errors.organizationName?.message} required>
          {(a) => <Input autoComplete="organization" autoFocus {...a} {...register('organizationName')} />}
        </FormField>
        <FormField label="Seu nome" error={errors.name?.message} required>
          {(a) => <Input autoComplete="name" {...a} {...register('name')} />}
        </FormField>
        <FormField label="E-mail" error={errors.email?.message} required>
          {(a) => <Input type="email" autoComplete="username" {...a} {...register('email')} />}
        </FormField>
        <FormField label="Senha" hint="Mínimo de 8 caracteres, com letra e número." error={errors.password?.message} required>
          {(a) => <PasswordInput autoComplete="new-password" {...a} {...register('password')} />}
        </FormField>
        <input type="hidden" {...register('timezone')} />
        <Controller
          name="acceptTerms"
          control={control}
          render={({ field }) => <TermsCheckbox checked={field.value === true} onCheckedChange={(v) => field.onChange(v ? true : undefined)} error={errors.acceptTerms?.message} />}
        />
        <Button type="submit" size="lg" loading={isSubmitting}>
          Criar conta
        </Button>
      </form>
      <p className="mt-6 text-sm text-muted-foreground">
        Já tem conta?{' '}
        <Link to="/entrar" className="font-medium text-primary underline-offset-4 hover:underline">
          Entrar
        </Link>
      </p>
    </>
  );
}
